import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import { useCart } from '../context/CartContext';
import { useBranding } from '../context/BrandingContext';
import { Link } from 'react-router-dom';
import { getUser } from '../utils/auth';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/common/SEO';

const Comparison = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language?.startsWith('ar');
    const { formatPrice } = useCurrency();
    const { addToCart } = useCart();
    const { branding } = useBranding();
    const profileId = branding?.profile_id || 'default';
    const user = getUser();
    const userId = user?.id || 'guest';
    const storageKey = `compare_items_${profileId}_${userId}`;
    const [compareItems, setCompareItems] = useState([]);

    useEffect(() => {
        const items = localStorage.getItem(storageKey);
        if (items) {
            try {
                setCompareItems(JSON.parse(items));
            } catch (e) {
                setCompareItems([]);
            }
        }
    }, [storageKey]);

    const removeFromCompare = (id) => {
        const newItems = compareItems.filter(item => item.id !== id);
        setCompareItems(newItems);
        localStorage.setItem(storageKey, JSON.stringify(newItems));
    };

    if (compareItems.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center animate-fade-in">
                <div className="w-24 h-24 mb-6 rounded-full bg-gray-50 flex items-center justify-center text-4xl text-gray-200">
                    <i className="fas fa-columns"></i>
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-4">{t('compare.empty', 'No products to compare')}</h2>
                <p className="text-gray-500 mb-8 max-w-md">{t('compare.empty_desc', 'Select products from the shop to compare their features and find the perfect match for your home.')}</p>
                <Link to="/shop" className="bg-brand-primary text-white px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-105 active:scale-95 transition-all">
                    {t('compare.browse_shop', 'Add Products')}
                </Link>
            </div>
        );
    }

    const features = [
        { key: 'price', label: t('compare.price', 'Price') },
        { key: 'material', label: t('compare.material', 'Material') },
        { key: 'category', label: t('compare.category', 'Category') },
        { key: 'dimensions', label: t('compare.dimensions', 'Dimensions') },
        { key: 'weight', label: t('compare.weight', 'Weight') },
        { key: 'color', label: t('compare.color', 'Color') }
    ];

    return (
        <>
            <Helmet>
                <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
            </Helmet>
            <SEO />
            <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
                <div className="flex items-center justify-between mb-12 pb-6 border-b border-gray-100">
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">{t('compare.title', 'Product Comparison')}</h1>
                    <button
                        onClick={() => { setCompareItems([]); localStorage.removeItem(storageKey); }}
                        className="text-gray-300 font-black text-[10px] uppercase tracking-widest hover:text-red-500 transition-all"
                    >
                        {t('compare.clear_all', 'Clear All')}
                    </button>
                </div>

                <div className="overflow-x-auto pb-10">
                    <table className="w-full min-w-[800px] border-collapse">
                        <thead>
                            <tr>
                                <th className={`w-64 p-6 bg-gray-50/50 ${isArabic ? 'rounded-tr-3xl text-right' : 'rounded-tl-3xl text-left'} font-black text-[10px] uppercase tracking-widest text-gray-400`}>
                                    {t('compare.features', 'Product Features')}
                                </th>
                                {compareItems.map((item, idx) => (
                                    <th key={item.id} className={`p-8 bg-white border-x border-gray-50 min-w-[280px] ${idx === compareItems.length - 1 ? 'rounded-tr-3xl' : ''}`}>
                                        <div className="relative group text-center">
                                            <button
                                                onClick={() => removeFromCompare(item.id)}
                                                className={`absolute ${isArabic ? '-top-4 -left-4' : '-top-4 -right-4'} w-8 h-8 rounded-full bg-white shadow-lg text-gray-300 hover:text-red-500 hover:scale-110 transition-all z-10`}
                                            >
                                                <i className="fas fa-times text-xs"></i>
                                            </button>
                                            <div className="w-40 h-40 mx-auto rounded-3xl overflow-hidden bg-gray-50 mb-6 shadow-xl shadow-gray-100 border border-gray-100">
                                                <img src={item.imageUrl || '/placeholder-furniture.jpg'} alt={isArabic ? item.name_ar : item.name} className="w-full h-full object-cover" />
                                            </div>
                                            <h3 className="text-lg font-black text-gray-900 mb-2 leading-tight">
                                                {isArabic && item.name_ar ? item.name_ar : item.name}
                                            </h3>
                                            <button
                                                onClick={() => addToCart(item)}
                                                className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-2.5 rounded-full font-black text-[9px] uppercase tracking-widest hover:bg-brand-primary transition-all active:scale-95"
                                            >
                                                <i className="fas fa-cart-plus"></i>
                                                {t('compare.add_to_cart', 'Add')}
                                            </button>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white border-b border-gray-50">
                            {features.map((feature) => (
                                <tr key={feature.key} className="group hover:bg-gray-50/30 transition-colors">
                                    <td className="p-6 border-r border-gray-50 font-bold text-xs text-gray-500 uppercase tracking-widest">
                                        {feature.label}
                                    </td>
                                    {compareItems.map((item) => (
                                        <td key={item.id} className="p-8 border-r border-gray-50 text-sm font-black text-gray-900 text-center">
                                            {feature.key === 'price' ? formatPrice(item) : (isArabic && item[`${feature.key}_ar`] ? item[`${feature.key}_ar`] : item[feature.key] || '—')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default Comparison;
